import {
  Node_Category,
  Node_Type,
  Edge_Category,
  Functionality_Base_Backend,
} from 'api/GenericPathwayInterfaces';

import {
  Community_Pathway_Backend,
  CommunityPathwayFlow,
  CommunityNodeFlow,
  CommunityEdgeFlow,
  CommunityFunctionalityFlow,
  Discipleship_Pathway_Backend,
  DiscipleshipPathwayFlow,
  DiscipleshipPathwayNodeFlow,
  DiscipleshipPathwayEdgeFlow,
  DiscipleshipFunctionalityFlow,
} from 'api/Community/CommunityInterfaces';

/**
 * Transform backend Community Pathway data into frontend Flow format
 */
export function transformCommunityPathwayToFlow(
  backendData: Community_Pathway_Backend,
): CommunityPathwayFlow {
  const nodes: CommunityNodeFlow[] = (backendData.nodes || []).map(node => {
    const nodeTypeObj: Node_Type | null =
      typeof node.node_type === 'object' ? node.node_type : null;

    const nodeCategory: Node_Category | null = nodeTypeObj?.node_category ?? null;

    // Explicitly treat functionalities as backend type
    const functionalities: CommunityFunctionalityFlow[] = (
      (node.functionalities || []) as unknown as Functionality_Base_Backend[]
    ).map(f => ({
      id: f.id,
      name: f.name,
      description: f.description,
      inputEdgeType: f.input_edge_type,
      inputLabel: f.input_label,
      inputHandleColor: f.input_handle_colour,
      outputEdgeType: f.output_edge_type,
      outputLabel: f.output_label,
      outputHandleColor: f.output_handle_colour,
      label: f.label,
      metadata: f.metadata,
      valueType: f.value_type,
      valueId: f.value_id,
      value: f.value,
    }));

    const newNode: CommunityNodeFlow = {
      id: node.id.toString(),
      position: {
        x: node.position_x ?? 0,
        y: node.position_y ?? 0,
      },
      type: 'default',
      style: {
        backgroundColor: nodeCategory?.colour_primary ?? '#999999',
        color: nodeCategory?.colour_text ?? '#FFFFFF',
      },

      // 🔥 UPDATED: people + functionalities moved inside `data`
      data: {
        name: node.name,
        description: node.description ?? '',
        type: node.node_type?.name ?? '',
        category: node.node_type?.node_category?.name ?? '',
        colorPrimary: nodeCategory?.colour_primary ?? '#999999',
        linkedNode: '',
        baseLink: node.linked_id ? `/nodes/` : '',
        linkedId: node.linked_id ? node.linked_id : '',

        people: node.people || [],
        peopleCount: node.people_count,
        addresses: node.addresses || [],
        addressesCount: node.addresses_count,
        functionalities: functionalities,
        itemTypes: node.node_type?.item_types,
      },
    };

    return newNode;
  });

  const edges: CommunityEdgeFlow[] = (backendData.edges || []).map(edge => {
    const edgeCategory: Edge_Category | null =
      typeof edge.edge_category === 'object' ? edge.edge_category : null;

    return {
      id: edge.id.toString(),
      type: 'smoothstep',
      source: edge.source.toString(),
      target: edge.target.toString(),
      sourceHandle: edge.source_handle,
      targetHandle: edge.target_handle,
      animated: edge.animated ?? false,
      data: {},
      style: edgeCategory
        ? {
            stroke: edgeCategory.colour_primary,
            color: edgeCategory.colour_text,
          }
        : undefined,
    };
  });

  return {
    summary: backendData.summary,
    nodes,
    edges,
  };
}

/**
 * Transform backend Discipleship Pathway data into frontend Flow format
 */
export function transformDiscipleshipPathwayToFlow(
  backendData: Discipleship_Pathway_Backend,
): DiscipleshipPathwayFlow {
  const nodes: DiscipleshipPathwayNodeFlow[] = (backendData.nodes || []).map(node => {

    const functionalities: DiscipleshipFunctionalityFlow[] = (
      (node.functionalities || []) as unknown as Functionality_Base_Backend[]
    ).map(f => ({
      id: f.id,
      name: f.name,
      description: f.description,
      inputEdgeType: f.input_edge_type,
      inputLabel: f.input_label,
      inputHandleColor: f.input_handle_colour,
      outputEdgeType: f.output_edge_type,
      outputLabel: f.output_label,
      outputHandleColor: f.output_handle_colour,
      label: f.label,
      metadata: f.metadata,
      valueType: f.value_type,
      valueId: f.value_id,
      value: f.value,
    }));


    const newNode: DiscipleshipPathwayNodeFlow = {
      id: node.id.toString(),
      position: {
        x: node.position_x ?? 0,
        y: node.position_y ?? 0,
      },
      type: 'default',
      style: {},
        
      data: {
        name: node.discipleship_stage.name,
        stage_id: node.discipleship_stage.id,
        description: node.discipleship_stage.description ?? '',
        people: node.people || [],
        peopleCount: node.people_count,
        colorPrimary: node.colour_primary,
        colorText: node.colour_text,
        functionalities: functionalities,
        itemTypes: node.node_type?.item_types,
      },
    };

    return newNode;
  });

  const edges: DiscipleshipPathwayEdgeFlow[] = (backendData.edges || []).map(edge => {
    const edgeCategory: Edge_Category | null =
      typeof edge.edge_category === 'object' ? edge.edge_category : null;

    return {
      id: edge.id.toString(),
      type: 'smoothstep',
      source: edge.source.toString(),
      target: edge.target.toString(),
      sourceHandle: edge.source_handle,
      targetHandle: edge.target_handle,
      animated: edge.animated ?? false,
      data: {},
      style: edgeCategory
        ? {
            stroke: edgeCategory.colour_primary,
            color: edgeCategory.colour_text,
          }
        : undefined,
    };
  });

  return {
    summary: backendData.summary,
    nodes,
    edges,
  };
}
